import { Component, HostBinding, inject, Input, OnInit } from '@angular/core';
import {
  AbstractComponent,
  AuthenticationService,
  ClipboardTools,
  NavigationService,
  SolButtonComponent,
  SolButtonType,
  TranslatePipe,
} from '../../../../public-api';
import { SolIcon } from '@sol/icons';
import { State } from './sol-error-page.state';
import { SolEmptyStateComponent } from '../sol-empty-state/sol-empty-state.component';
import { SolLayoutComponent } from '../sol-layout/sol-layout.component';
import { NavigationTarget, RouterTools } from '../../../tools/router-tools';
import { SolButton } from '../../../models/core/sol-button';

export enum SolErrorPageType {
  UNKNOWN = 'unknown',
  NOT_FOUND = 'not-found',
  CONNECTION_ERROR = 'connection-error',
  FORBIDDEN = 'forbidden',
}

@Component({
  selector: 'sol-error-page',
  imports: [SolButtonComponent, TranslatePipe, SolEmptyStateComponent, SolLayoutComponent],
  templateUrl: './sol-error-page.component.html',
})
export class SolErrorPageComponent extends AbstractComponent<State> implements OnInit {
  protected readonly SolIcon = SolIcon;

  // Dependencies
  private readonly authenticationService: AuthenticationService = inject(AuthenticationService);
  private readonly navigationService = inject(NavigationService);

  // Computed
  protected readonly emptyState$ = this.compute(state => state.emptyState);
  protected readonly messages$ = this.compute(state => state.messages);
  protected readonly showUsername$ = this.compute(state => state.showUsername);
  protected readonly username$ = this.compute(state => state.username);
  protected readonly hostClass$ = this.compute(state => state.hostClass);

  // Binding
  protected readonly copyButton = SolButton.of('', SolIcon.copy).withType(SolButtonType.transparent);

  // Cache
  private inputType?: SolErrorPageType;
  private inputCallbackUri?: string;

  constructor() {
    super(State.of());

    this.authenticationService.watchComputed(
      auth => [auth.email, auth.enabled] as [string | undefined, boolean],
      ([email, enabled]) => this.refresh(enabled, email ?? '<unknown>'),
    );

    this.navigationService.watchComputed(
      nss => {
        const app = RouterTools.findCurrentApp(nss);
        return app?.hasAuthority ? app.id : undefined;
      },
      appRouteId => this.update(state => state.withAppRouteId(appRouteId)),
    );

    this.refresh(this.authenticationService.state.enabled, this.authenticationService.state.email ?? '<unknown>');
  }

  @Input()
  public set type(type: SolErrorPageType | undefined) {
    if (type === this.inputType) {
      return;
    }
    this.inputType = type;
    this.refresh();
  }

  @Input()
  public set callbackUri(callbackUri: string | undefined) {
    if (callbackUri === this.inputCallbackUri) {
      return;
    }
    this.inputCallbackUri = callbackUri;
    this.refresh();
  }

  @HostBinding('class')
  public get hostClass(): string {
    return this.hostClass$();
  }

  ngOnInit(): void {
    this.authenticationService.fetch();
  }

  protected onCopy(): void {
    ClipboardTools.copyText(this.state.username ?? '');
  }

  protected onAction(): void {
    const type = this.state.type;
    switch (type) {
      case SolErrorPageType.NOT_FOUND:
      case SolErrorPageType.CONNECTION_ERROR:
        this.goToCallbackUri();
        break;
      case SolErrorPageType.FORBIDDEN:
        this.goToAppOrLogout();
        break;
      default:
        this.navigationService.navigateHome();
        break;
    }
  }

  private goToCallbackUri(): void {
    const callbackUri = this.state.callbackUri;
    if (callbackUri) {
      this.navigationService.navigateByUrl(callbackUri, { target: NavigationTarget.SameTab });
    } else {
      this.navigationService.navigateHome();
    }
  }

  private goToAppOrLogout(): void {
    const appRouteId = this.state.appRouteId;
    if (appRouteId !== undefined) {
      this.navigationService.navigate(appRouteId);
    } else {
      this.logout();
    }
  }

  protected logout(): void {
    this.authenticationService.logout();
  }

  private refresh(authenticationEnabled?: boolean, username?: string): void {
    this.update(current => {
      const nextState = username !== undefined ? current.withUsername(username) : current;
      const authEnabled = authenticationEnabled ?? this.authenticationService.state.enabled;
      return nextState.withResolved(this.resolveType(), this.resolveCallback(), authEnabled);
    });
  }

  private resolveType(): SolErrorPageType {
    return this.inputType ?? SolErrorPageType.UNKNOWN;
  }

  private resolveCallback(): string | undefined {
    return this.inputCallbackUri;
  }
}
